import { RadioButtonUnchecked } from '@mui/icons-material'
import CheckIcon from '@mui/icons-material/Check'
import { Box, Typography } from '@mui/material'
import { EthDate } from '@shared/conversions'
import React, { FC } from 'react'
import { useOtChainTimestamp } from '@react/redux/store'
import { formatDate, formatDateTime } from '@react/utils/dates'

export interface TimeLineBlockStruct {
  eventDate: EthDate
  header: string
  caption?: string
  completed: boolean
  completedTimestamp?: EthDate
}

interface TimeLineBlockProps {
  block: TimeLineBlockStruct
  theme: any
  isLast?: boolean
}
const ONE_DAY = 3600 * 24

export const TimeLineBlock: FC<TimeLineBlockProps> = ({ block, theme, isLast = false }): React.ReactElement => {
  const chainTimestamp = useOtChainTimestamp()
  let dateString = formatDate(block.eventDate)
  const completedDateString = formatDateTime(block.completedTimestamp ?? 0)

  if (!block.completed && block.eventDate < chainTimestamp + 2 * ONE_DAY && chainTimestamp < block.eventDate) {
    const hoursDiff = Math.floor((block.eventDate - chainTimestamp) / 3600)
    dateString = dateString + ` -  ${hoursDiff} hours remaining`
  }
  if (chainTimestamp > block.eventDate && !block.completed) {
    const hoursDiff = Math.floor((chainTimestamp - block.eventDate) / 3600)
    dateString = dateString + `EXPIRED -  ${hoursDiff} hours ago`
  }
  function checkIcon(): React.ReactElement {
    return (
      <>
        <Box
          sx={{
            position: 'absolute',
            top: -10,
            left: 5,
            backgroundColor: '#fff',
            borderRadius: 12,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {block.completed ? (
            <CheckIcon sx={{ color: theme.palette.success.main, zIndex: 1 }} data-testid="event-complete-icon" />
          ) : (
            <RadioButtonUnchecked fontSize={'medium'} sx={{ zIndex: 1 }} data-testid="event-not-complete-icon" />
          )}
        </Box>
      </>
    )
  }
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          margin: '0px 0px 24px',
          gap: '16px',
          isolation: 'isolate',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            top: 20,
            left: 0,
            // bottom: isLast ? 24 : 0, // added bottom property
            width: '1px',
            backgroundColor: '#9E9E9E', // changed border property to backgroundColor
          }}
        />
        {checkIcon()}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '0px',
            gap: '8px',
            paddingLeft: '20px',
            marginTop: '-10px',
            borderLeft: isLast ? 'none' : '1px solid #000',
          }}
        >
          <Typography variant={'small'} marginBottom={1}>
            {dateString}
          </Typography>
          {block.completedTimestamp != null && (
            <Typography variant={'bodyMuted'} sx={{ fontSize: 12 }} marginBottom={1} marginTop={-4}>
              {completedDateString}
            </Typography>
          )}
          <Typography variant={'smallBold'} my={1}>
            {block.header}
          </Typography>
          <Typography variant={'small'} my={1}>
            {block.caption}
          </Typography>
        </Box>
      </Box>
    </>
  )
}
